import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Eye, EyeOff, CheckCircle, AlertCircle, Lock, HardHat,
  Briefcase, Building2, Mail, Clock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import { checkPasswordRules } from '../lib/passwordPolicy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasswordRule = ({ ok, label }) => (
  <div className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>
    {ok ? <CheckCircle className="h-3 w-3" /> : <div className="h-3 w-3 rounded-full border border-gray-300" />}
    {label}
  </div>
);

const STATUS_VIEW = ({ icon: Icon, title, message, onAction, actionLabel, tone = 'red' }) => {
  const toneCls = tone === 'red'
    ? { ring: 'bg-red-100', icon: 'text-red-600' }
    : tone === 'emerald'
    ? { ring: 'bg-emerald-100', icon: 'text-emerald-600' }
    : { ring: 'bg-amber-100', icon: 'text-amber-600' };
  return (
    <div className="min-h-screen bg-[#F7F5EF] flex flex-col notranslate" translate="no">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl border border-[rgba(10,58,90,0.08)] p-10 max-w-md w-full text-center">
          <div className={`h-20 w-20 ${toneCls.ring} rounded-full flex items-center justify-center mx-auto mb-5`}>
            <Icon className={`h-10 w-10 ${toneCls.icon}`} />
          </div>
          <h2 className="font-playfair text-2xl md:text-3xl text-[#0A3A5A] font-medium mb-3 tracking-tight">{title}</h2>
          <p className="text-[#4A5568] text-sm mb-6 leading-relaxed">{message}</p>
          <Button
            data-testid="contractor-setpw-status-cta"
            onClick={onAction}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8"
          >
            {actionLabel}
          </Button>
        </div>
      </div>
      <div className="py-5 text-center text-[11px] text-[#5A6B7D]">© 2026 COP 31 · Antalya</div>
    </div>
  );
};

export default function ContractorSetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [pageState, setPageState] = useState('loading');
  const [invite, setInvite] = useState(null);

  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [passTouched, setPassTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const rules = checkPasswordRules(form.password);
  const allRulesOk = Object.values(rules).every(Boolean);
  const passwordsMatch = form.password === form.confirm;

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const r = await axios.get(`${API}/auth/contractor-invite/validate`, { params: { token } });
        setInvite(r.data);
        setPageState('form');
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail || '';
        if (status === 410 && /kullan/i.test(detail)) setPageState('used');
        else if (status === 410) setPageState('expired');
        else setPageState('invalid');
      }
    };
    fetchInfo();
  }, [token]);

  const handleSubmit = async () => {
    setError('');
    if (!allRulesOk) {
      setError(tr ? 'Şifre kurallarına uygun değil.' : 'Password does not meet requirements.');
      setPassTouched(true);
      return;
    }
    if (!passwordsMatch) {
      setError(tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.');
      setConfirmTouched(true);
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/auth/contractor-set-password`, {
        token,
        password: form.password,
      });
      const data = r.data;
      localStorage.setItem('cop31_token', data.access_token);
      localStorage.setItem('cop31_user', JSON.stringify(data));
      setPageState('success');
      setTimeout(() => {
        const slug = data.tenant_slug || 'default';
        navigate(`/yuklenci-admin/${slug}/dashboard`, { replace: true });
      }, 1200);
    } catch (err) {
      setError(err.response?.data?.detail || (tr ? 'Şifre belirlenemedi.' : 'Could not set password.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-[#F7F5EF] flex items-center justify-center notranslate" translate="no">
        <div className="text-center">
          <div className="h-10 w-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5A6B7D] text-sm">{tr ? 'Davet doğrulanıyor...' : 'Validating invitation...'}</p>
        </div>
      </div>
    );
  }

  if (pageState === 'expired') {
    return (
      <STATUS_VIEW
        icon={Clock}
        title={tr ? 'Davet Süresi Doldu' : 'Invitation Expired'}
        message={tr
          ? 'Bu davet bağlantısının 7 günlük geçerlilik süresi dolmuştur. Lütfen Ultra Admin ile iletişime geçin.'
          : 'This invitation link has expired (7-day validity). Please contact the Ultra Admin.'}
        onAction={() => navigate('/giris?tab=contractor&only=1')}
        actionLabel={tr ? 'Giriş Sayfasına Git' : 'Go to Login'}
        tone="amber"
      />
    );
  }

  if (pageState === 'used') {
    return (
      <STATUS_VIEW
        icon={CheckCircle}
        title={tr ? 'Bu Bağlantı Kullanıldı' : 'Link Already Used'}
        message={tr
          ? 'Hesabınızın şifresi daha önce belirlenmiş. Lütfen mevcut şifrenizle giriş yapın.'
          : 'You have already set your password. Please log in with your existing credentials.'}
        onAction={() => navigate('/giris?tab=contractor&only=1')}
        actionLabel={tr ? 'Giriş Yap' : 'Log In'}
        tone="emerald"
      />
    );
  }

  if (pageState === 'invalid') {
    return (
      <STATUS_VIEW
        icon={AlertCircle}
        title={tr ? 'Geçersiz Bağlantı' : 'Invalid Link'}
        message={tr
          ? 'Bu davet bağlantısı geçersiz. E-postanızdaki tam bağlantıyı kontrol edin.'
          : 'This invitation link is not valid. Please check the full URL from your email.'}
        onAction={() => navigate('/ana-sayfa')}
        actionLabel={tr ? 'Ana Sayfa' : 'Home'}
        tone="red"
      />
    );
  }

  if (pageState === 'success') {
    return (
      <STATUS_VIEW
        icon={CheckCircle}
        title={tr ? 'Şifreniz Belirlendi!' : 'Password Set!'}
        message={tr
          ? 'Hesabınız aktive edildi. Müteahhit panelinize yönlendiriliyorsunuz...'
          : 'Your account is active. Redirecting you to your contractor dashboard...'}
        onAction={() => {}}
        actionLabel={tr ? 'Yönlendiriliyor...' : 'Redirecting...'}
        tone="emerald"
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5EF] flex flex-col notranslate" translate="no">
      <div className="flex items-center justify-between px-6 py-4 shrink-0">
        <button
          onClick={() => navigate('/giris?tab=contractor&only=1')}
          className="flex items-center gap-2 text-[#0A3A5A]/70 hover:text-[#0A3A5A] transition-colors text-sm font-medium"
          data-testid="contractor-setpw-back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          {tr ? 'Giriş' : 'Login'}
        </button>
        <div className="text-[#0A3A5A] font-playfair text-2xl tracking-tight italic">COP 31 · Antalya</div>
        <LanguageToggle variant="default" />
      </div>

      <div className="flex-1 flex items-start justify-center px-4 pb-10 pt-2">
        <div className="w-full max-w-lg">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-red-100 border border-red-200 mb-3">
              <HardHat className="h-7 w-7 text-red-600" />
            </div>
            <h1 className="font-playfair text-3xl md:text-4xl text-[#0A3A5A] font-medium mb-1 tracking-tight">
              {tr ? 'Müteahhit Hesabı Aktivasyonu' : 'Contractor Account Activation'}
            </h1>
            <p className="text-[#4A5568] text-sm">
              {tr
                ? 'COP 31 Antalya Müteahhit Platformu\'na hoş geldiniz'
                : 'Welcome to the COP 31 Antalya Contractor Platform'}
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-[rgba(10,58,90,0.08)] overflow-hidden">
            <div className="p-7 space-y-5">

              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 space-y-2.5">
                <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">
                  {tr ? 'Hesap Bilgileri' : 'Your Account'}
                </p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-red-500 shrink-0" />
                    <span className="text-gray-500 min-w-[80px]">{tr ? 'Firma' : 'Company'}:</span>
                    <span className="font-semibold text-gray-800 truncate" data-testid="contractor-setpw-company">{invite?.company}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-red-500 shrink-0" />
                    <span className="text-gray-500 min-w-[80px]">{tr ? 'E-posta' : 'Email'}:</span>
                    <span className="font-semibold text-gray-800 truncate" data-testid="contractor-setpw-email">{invite?.email}</span>
                  </div>
                  {invite?.contractor_category_name && (
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-red-500 shrink-0" />
                      <span className="text-gray-500 min-w-[80px]">{tr ? 'Kategori' : 'Category'}:</span>
                      <span className="font-semibold text-gray-800 truncate" data-testid="contractor-setpw-category">
                        {invite.contractor_category_name}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <div className="h-8 w-8 rounded-xl bg-red-100 flex items-center justify-center">
                  <Lock className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-800 text-base">{tr ? 'Şifre Belirleyin' : 'Set Your Password'}</h2>
                  <p className="text-xs text-gray-400">{tr ? 'Tamamlandıktan sonra otomatik giriş yapılır.' : 'You will be logged in automatically.'}</p>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm text-gray-700">{tr ? 'Yeni Şifre' : 'New Password'} <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    data-testid="contractor-setpw-password"
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    onBlur={() => setPassTouched(true)}
                    className={`rounded-xl pr-10 ${(passTouched && !allRulesOk && form.password) ? 'border-red-400' : 'border-gray-200'}`}
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {(passTouched || form.password.length > 0) && (
                  <div className="grid grid-cols-2 gap-1 pt-1">
                    <PasswordRule ok={rules.length}    label={tr ? 'En az 8 karakter' : 'Min 8 chars'} />
                    <PasswordRule ok={rules.uppercase} label={tr ? 'Büyük harf'      : 'Uppercase'} />
                    <PasswordRule ok={rules.lowercase} label={tr ? 'Küçük harf'      : 'Lowercase'} />
                    <PasswordRule ok={rules.number}    label={tr ? 'Rakam'           : 'Number'} />
                    <PasswordRule ok={rules.special}   label={tr ? 'Özel karakter'   : 'Special char'} />
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm text-gray-700">{tr ? 'Şifre Tekrar' : 'Confirm Password'} <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    data-testid="contractor-setpw-confirm"
                    type={showPw2 ? 'text' : 'password'}
                    value={form.confirm}
                    onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                    onBlur={() => setConfirmTouched(true)}
                    className={`rounded-xl pr-10 ${(confirmTouched && form.confirm && !passwordsMatch) ? 'border-red-400' : confirmTouched && passwordsMatch && form.confirm ? 'border-emerald-400' : 'border-gray-200'}`}
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowPw2(!showPw2)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                    {showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmTouched && form.confirm && !passwordsMatch && (
                  <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3 w-3" />
                    {tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.'}
                  </p>
                )}
                {confirmTouched && form.confirm && passwordsMatch && allRulesOk && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                    <CheckCircle className="h-3 w-3" />
                    {tr ? 'Şifreler eşleşiyor.' : 'Passwords match.'}
                  </p>
                )}
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <Button
                data-testid="contractor-setpw-submit"
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !form.password || !form.confirm}
                className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl py-5 font-semibold text-sm mt-2"
              >
                {submitting
                  ? <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      {tr ? 'İşleniyor...' : 'Processing...'}
                    </span>
                  : (tr ? 'Şifreyi Belirle ve Giriş Yap' : 'Set Password & Log In')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="py-5 text-center text-[11px] text-[#5A6B7D]">© 2026 COP 31 · Antalya</div>
    </div>
  );
}
